import { z } from "zod";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Cookies from "js-cookie";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form/form";

import { useRemovePermissionModal } from "./use-permission-modal";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { Button } from "@/components/ui/button/button";
import { editEmployeePermissions } from "@/config/mutation/admin";
import { toast } from "sonner";
import { AxiosError } from "axios";

interface FormSchemaType {
  remove: {
    permissionType: string;
    doc: string;
  }[];
}

const FormSchema: z.ZodType<FormSchemaType> = z.object({
  remove: z.array(
    z.object({
      permissionType: z.string(),
      doc: z.string(),
    })
  ),
});

type FormValues = z.infer<typeof FormSchema>;

export const RemovePermissionsModal = () => {
  const key = Cookies.get("key") as string;

  const {
    open,
    onOpenChange,
    permissions: alreadyPermissions,
    employeeId,
  } = useRemovePermissionModal();

  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      remove: [],
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: editEmployeePermissions(key),
  });

  const onSubmit = (data: FormValues) => {
    mutate(
      {
        empId: employeeId,
        info: {
          remove: data?.remove,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["employees"],
          });
          toast.success("Permissions removed!");

          onOpenChange(false);

          setTimeout(() => {
            form.reset({
              remove: [],
            });
          }, 1000);
        },

        onError: (error) => {
          console.log("ERROR WHILE EDITING PERMISSIONS", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  const selectedPermissions = useWatch({
    name: "remove",
    control: form.control,
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Permissions</DialogTitle>
          </DialogHeader>

          <div className="mt-5">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="space-y-7">
                  <div className="flex flex-col gap-1">
                    <h3 className="underline underline-offset-2">
                      Read Permissions
                    </h3>

                    {alreadyPermissions?.["1"]?.length > 0 ? (
                      <>
                        <FormField
                          name="remove"
                          control={form.control}
                          render={({ field }) => {
                            return (
                              <FormItem className="flex items-center gap-5 flex-wrap space-y-0 mt-3">
                                {alreadyPermissions?.["1"]?.map((perm) => {
                                  const permType = "1";
                                  return (
                                    <FormField
                                      name="remove"
                                      control={form.control}
                                      key={permType + perm}
                                      render={() => {
                                        return (
                                          <FormItem
                                            key={perm}
                                            className="flex flex-row items-center gap-2 space-y-0"
                                          >
                                            <FormControl>
                                              <Checkbox
                                                checked={field.value?.some(
                                                  (obj) =>
                                                    obj.permissionType ===
                                                      permType &&
                                                    obj.doc === perm
                                                )}
                                                onCheckedChange={(checked) => {
                                                  const updatedPermissions =
                                                    checked
                                                      ? [
                                                          ...field.value,
                                                          {
                                                            permissionType:
                                                              permType,
                                                            doc: perm,
                                                          },
                                                        ]
                                                      : field.value.filter(
                                                          (p) =>
                                                            !(
                                                              p.permissionType ===
                                                                permType &&
                                                              p.doc === perm
                                                            )
                                                        );
                                                  field.onChange(
                                                    updatedPermissions
                                                  );
                                                }}
                                              />
                                            </FormControl>
                                            <FormLabel className="text-sm font-normal">
                                              {perm}
                                            </FormLabel>
                                          </FormItem>
                                        );
                                      }}
                                    />
                                  );
                                })}
                              </FormItem>
                            );
                          }}
                        />
                      </>
                    ) : (
                      <div>No Permissions given</div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <h3 className="underline underline-offset-2">
                      Edit Permissions
                    </h3>

                    {alreadyPermissions?.["2"]?.length > 0 ? (
                      <>
                        <FormField
                          name="remove"
                          control={form.control}
                          render={({ field }) => {
                            return (
                              <FormItem className="flex items-center gap-5 flex-wrap space-y-0 mt-3">
                                {alreadyPermissions?.["2"]?.map((perm) => {
                                  const permType = "2";
                                  return (
                                    <FormField
                                      name="remove"
                                      control={form.control}
                                      key={permType + perm}
                                      render={() => {
                                        return (
                                          <FormItem
                                            key={perm}
                                            className="flex flex-row items-center gap-2 space-y-0"
                                          >
                                            <FormControl>
                                              <Checkbox
                                                checked={field.value?.some(
                                                  (obj) =>
                                                    obj.permissionType ===
                                                      permType &&
                                                    obj.doc === perm
                                                )}
                                                onCheckedChange={(checked) => {
                                                  const updatedPermissions =
                                                    checked
                                                      ? [
                                                          ...field.value,
                                                          {
                                                            permissionType:
                                                              permType,
                                                            doc: perm,
                                                          },
                                                        ]
                                                      : field.value.filter(
                                                          (p) =>
                                                            !(
                                                              p.permissionType ===
                                                                permType &&
                                                              p.doc === perm
                                                            )
                                                        );

                                                  field.onChange(
                                                    updatedPermissions
                                                  );
                                                }}
                                              />
                                            </FormControl>
                                            <FormLabel className="text-sm font-normal">
                                              {perm}
                                            </FormLabel>
                                          </FormItem>
                                        );
                                      }}
                                    />
                                  );
                                })}
                              </FormItem>
                            );
                          }}
                        />
                      </>
                    ) : (
                      <div>No Permissions given</div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <h3 className="underline underline-offset-2">
                      Remove/Delete Permissions
                    </h3>

                    {alreadyPermissions?.["3"]?.length > 0 ? (
                      <>
                        <FormField
                          name="remove"
                          control={form.control}
                          render={({ field }) => {
                            return (
                              <FormItem className="flex items-center gap-5 flex-wrap space-y-0 mt-3">
                                {alreadyPermissions?.["3"]?.map((perm) => {
                                  const permType = "3";
                                  return (
                                    <FormField
                                      name="remove"
                                      control={form.control}
                                      key={permType + perm}
                                      render={() => {
                                        return (
                                          <FormItem
                                            key={perm}
                                            className="flex flex-row items-center gap-2 space-y-0"
                                          >
                                            <FormControl>
                                              <Checkbox
                                                checked={field.value?.some(
                                                  (obj) =>
                                                    obj.permissionType ===
                                                      permType &&
                                                    obj.doc === perm
                                                )}
                                                onCheckedChange={(checked) => {
                                                  const updatedPermissions =
                                                    checked
                                                      ? [
                                                          ...field.value,
                                                          {
                                                            permissionType:
                                                              permType,
                                                            doc: perm,
                                                          },
                                                        ]
                                                      : field.value.filter(
                                                          (p) =>
                                                            !(
                                                              p.permissionType ===
                                                                permType &&
                                                              p.doc === perm
                                                            )
                                                        );
                                                  field.onChange(
                                                    updatedPermissions
                                                  );
                                                }}
                                              />
                                            </FormControl>
                                            <FormLabel className="text-sm font-normal">
                                              {perm}
                                            </FormLabel>
                                          </FormItem>
                                        );
                                      }}
                                    />
                                  );
                                })}
                              </FormItem>
                            );
                          }}
                        />
                      </>
                    ) : (
                      <div>No Permissions given</div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end">
                  <Button
                    variant="destructive"
                    type="submit"
                    className="mt-3"
                    disabled={selectedPermissions.length === 0 || isPending}
                  >
                    Remove
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
